import {
	LOAD_CONVERSATIONS,
	LOAD_CONVERSATION,
	SEND_MESSAGE,
	MESSAGE_RECEIVED,
	MESSAGE_READ,
	NEW_CONVERSATION,
	SELECT_CONVERSATION,
	CONVERSATION_CREATED,
	CONVERSATION_ADDED,
	LOAD_PREVIOUS_MESSAGES,
	LOAD_MORE_CONVERSATIONS,
	CONVERSATIONS_LIMIT,
	CONVERSATION_READ,
	CLOSE_COMPOSER,
	MESSAGE_DELETED,
} from "../actions/chat";

import ConversationsMap from "../libs/conversationsMap";
import { ChatConversation, Conversation, Message } from "../types";
import { PROFILE_UPDATED, UNAUTHENTICATED } from "../actions";

export const initialState = {
	conversations: new ConversationsMap(),
	activeConversation: null as ChatConversation,
	activeConversationId: null as string,
	oldestMessageTimestamp: 8640000000000000,
	hasMoreConversations: false,
	unreadConversationsCount: 0,
	lastUpdate: null as number,
	myId: null as string,
	loading: true,
	composerOpen: false,
};

export type ChatState = typeof initialState;

export default function(state = initialState, action) {
	switch (action.type) {
		case LOAD_CONVERSATIONS: {
			const conversations = ConversationsMap.fromResponse(action.conversations);

			let activeConversation = null;
			let activeConversationId = null;
			if (!state.composerOpen) {
				const lastConversation = conversations.getLast();

				activeConversation = state.activeConversation;

				if (activeConversation === null) {
					activeConversation = lastConversation;
				}

				activeConversationId = activeConversation ? activeConversation.conversationId : null;
			}
			const oldestMessage = conversations.getOldestMessage();

			if (oldestMessage) {
				const oldestMessageTimestamp = new Date(oldestMessage.createdAt).valueOf();
				const hasMoreConversations = conversations.count() >= CONVERSATIONS_LIMIT;

				return {
					...state,
					conversations,
					activeConversation,
					activeConversationId,
					oldestMessageTimestamp,
					hasMoreConversations,
					unreadConversationsCount: conversations.getUnreadConversationsCount(),
					lastUpdate: Date.now(),
					loading: false,
				};
			} else {
				return {
					...state,
					conversations,
					lastUpdate: Date.now(),
					loading: false,
				};
			}
		}

		case LOAD_CONVERSATION: {
			const conversations = state.conversations.getCopy();

			const conversation = conversations.add(action.conversation);
			if (!conversation) {
				return state;
			}

			return {
				...state,
				conversations,
				activeConversation: conversation,
				activeConversationId: conversation.conversationId,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				composerOpen: false,
				//lastUpdate: Date.now(),
			};
		}

		case LOAD_MORE_CONVERSATIONS: {
			const conversations = state.conversations.getCopy();

			action.conversations.forEach((conversation: Conversation) => {
				conversations.add(conversation);
			});

			const oldestMessage = conversations.getOldestMessage();
			const oldestMessageTimestamp = new Date(oldestMessage.createdAt).valueOf();

			const hasMoreConversations = action.conversations.length >= CONVERSATIONS_LIMIT;

			return {
				...state,
				conversations,
				oldestMessageTimestamp,
				hasMoreConversations,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				lastUpdate: Date.now(),
			};
		}

		case SEND_MESSAGE: {
			return state;
		}

		case CONVERSATION_CREATED: {
			const conversations = state.conversations.getCopy();

			const conversation = conversations.add(action.conversation);
			if (!conversation) {
				return state;
			}

			return {
				...state,
				conversations,
				activeConversation: conversation,
				activeConversationId: conversation.conversationId,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				lastUpdate: Date.now(),
			};
		}

		case CONVERSATION_ADDED: {
			const conversations = state.conversations;

			const conversation = conversations.add(action.conversation);
			if (!conversation) {
				return state;
			}

			return {
				...state,
				conversations: conversations.getCopy(),
				activeConversation: state.activeConversation ? state.activeConversation : conversation,
				activeConversationId: state.activeConversation
					? state.activeConversationId
					: conversation.conversationId,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				lastUpdate: Date.now(),
			};
		}

		case MESSAGE_RECEIVED: {
			const conversations = state.conversations;
			const message = action.payload as Message;

			if (!conversations.has(message.conversationId)) {
				return state;
			}

			const conversation = conversations.get(message.conversationId);

			let increaseUnreadCount = true;
			if (message.senderId === state.myId) {
				increaseUnreadCount = false;
			}

			conversations.addMessage(message, increaseUnreadCount);

			let activeConversation = state.activeConversation;

			if (activeConversation) {
				if (activeConversation.conversationId === conversation.conversationId) {
					activeConversation = conversations.get(activeConversation.conversationId);
				}
			}

			return {
				...state,
				conversations: conversations.getCopy(),
				activeConversation,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				lastUpdate: Date.now(),
			};
		}

		case LOAD_PREVIOUS_MESSAGES: {
			const conversations = state.conversations.getCopy();
			const messages = action.messages;

			if (messages.length === 0) {
				return state;
			}

			const conversation = conversations.get(action.conversationId);

			if (conversation === null || conversation === undefined) {
				return state;
			}

			conversations.addOlderMessages(conversation.conversationId, messages);

			let activeConversation = null;
			let activeConversationId = null;
			if (!state.composerOpen) {
				const lastConversation = conversations.getLast();

				activeConversation = state.activeConversation;

				if (activeConversation === null) {
					activeConversation = lastConversation;
				}

				activeConversationId = activeConversation ? activeConversation.conversationId : null;
			}

			if (activeConversation) {
				if (activeConversation.conversationId === conversation.conversationId) {
					activeConversation = conversations.get(activeConversation.conversationId);
				}
			}

			return {
				...state,
				conversations: conversations.getCopy(),
				activeConversationId: activeConversationId,
				activeConversation,
				lastUpdate: Date.now(),
			};
		}

		case MESSAGE_READ: {
			const conversations = state.conversations.getCopy();
			const message = action.payload;

			const conversation = conversations.get(message.conversationId);

			if (conversation === null || conversation === undefined) {
				return state;
			}

			conversations.addRead(message);

			let activeConversation = state.activeConversation;

			if (activeConversation) {
				if (activeConversation.conversationId === conversation.conversationId) {
					activeConversation = conversations.get(activeConversation.conversationId);
				}
			}

			return {
				...state,
				conversations: conversations.getCopy(),
				activeConversationId: activeConversation.conversationId,
				activeConversation,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				lastUpdate: Date.now(),
			};
		}

		case MESSAGE_DELETED: {
			const conversations = state.conversations.getCopy();
			const message = action.payload;

			const conversation = conversations.get(message.conversationId);

			if (conversation === null || conversation === undefined) {
				return state;
			}

			conversations.deleteMessage(message);

			let activeConversation = state.activeConversation;

			if (activeConversation) {
				if (activeConversation.conversationId === conversation.conversationId) {
					activeConversation = conversations.get(activeConversation.conversationId);
				} else {
					//conversations.removeMessage(message);
				}
			}

			return {
				...state,
				conversations: conversations.getCopy(),
				activeConversationId: activeConversation.conversationId,
				activeConversation,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				lastUpdate: Date.now(),
			};
		}

		case CONVERSATION_READ: {
			const conversations = state.conversations.getCopy();

			const conversation = conversations.get(action.conversationId);

			if (conversation === null || conversation === undefined) {
				return state;
			}

			conversation.markAllRead();

			return {
				...state,
				conversations: conversations.getCopy(),
				//activeConversationId: activeConversation.conversationId,
				activeConversation: conversation,
				unreadConversationsCount: conversations.getUnreadConversationsCount(),
				lastUpdate: Date.now(),
			};
		}

		case NEW_CONVERSATION: {
			return {
				...state,
				activeConversation: null,
				activeConversationId: null,
				composerOpen: true,
				lastUpdate: Date.now(),
			};
		}

		case CLOSE_COMPOSER: {
			return {
				...state,
				composerOpen: false,
				lastUpdate: Date.now(),
			};
		}

		case SELECT_CONVERSATION: {
			if (action.id === state.activeConversationId) {
				return state;
			}

			const activeConversation = state.conversations.get(action.id);

			return {
				...state,
				activeConversation,
				activeConversationId: activeConversation.conversationId,
				composerOpen: false,
				lastUpdate: Date.now(),
			};
		}
		/*
		case "chat/ACK": {
			console.log(action);
			return state;
		}
		*/
		case "chat/SEND_ERROR": {
			console.log(action);
			return state;
		}

		case PROFILE_UPDATED: {
			return {
				...state,
				myId: action.profile.userId,
			};
		}

		case UNAUTHENTICATED: {
			return initialState;
		}

		default:
			return state;
	}
}
